import Vue from "vue";
import VueRouter from "vue-router";

interface VersionInfo {
    version?: string;
}

/** The UI build's git sha injected at build time; null for an unstamped ("dev") local build. */
export const stampedVersion: string | null = __APP_VERSION__ !== "dev" ? __APP_VERSION__ : null;

/** Reactive so layout components can also react to a pending update if they want to. */
export const updateState = Vue.observable({
    updateAvailable: false
});

const CHECK_INTERVAL_MS = 15 * 60_000;
/** Minimum gap between checks triggered by tab refocus, so alt-tabbing doesn't spam version.json. */
const FOCUS_CHECK_MIN_GAP_MS = 5 * 60_000;
/** Minimum gap between checks triggered by hub reconnects, so a flapping connection doesn't spam. */
const RECONNECT_CHECK_MIN_GAP_MS = 60_000;
/** One forced reload/navigation per window. If the CDN keeps serving the old bundle, a shorter
 *  guard would put the user in a reload loop; after one attempt we fall back to the toast. */
const FORCED_RELOAD_GUARD_MS = 5 * 60_000;
const FORCED_RELOAD_GUARD_KEY = "updateCheck:lastForcedReload";

/** Test seam: vitest runs with PROD=false and its own sha, so specs override these. */
export const _runtime = {
    isEnabled: (): boolean => import.meta.env.PROD && stampedVersion !== null,
    currentVersion: (): string => __APP_VERSION__,
    reload: (): void => window.location.reload(),
    navigate: (href: string): void => window.location.assign(href),
    isTourActive: (): boolean => rootApp?.$data?.tourActive === true
};

let rootApp: Vue | null = null;
let lastCheckAt = 0;
let toastShown = false;
/** Destination of the SPA navigation in flight, so a chunk failure lands the user where they were going. */
let pendingNavigationHref: string | null = null;

function recentlyForcedReload(): boolean {
    try {
        const last = Number(sessionStorage.getItem(FORCED_RELOAD_GUARD_KEY));
        return Number.isFinite(last) && Date.now() - last < FORCED_RELOAD_GUARD_MS;
    } catch {
        // sessionStorage unavailable: claim a recent reload so we can never loop.
        return true;
    }
}

function markForcedReload(): void {
    try {
        sessionStorage.setItem(FORCED_RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
        // Ignore; recentlyForcedReload already fails closed.
    }
}

function showUpdateToast(): void {
    if (toastShown || !rootApp) return;
    toastShown = true;

    const h = rootApp.$createElement;
    const body = h("div", { class: "d-flex align-items-center justify-content-between" }, [
        h("span", { class: "mr-3" }, "A new version of Scriptly is available."),
        h("b-button", {
            props: { variant: "primary", size: "sm" },
            on: { click: () => _runtime.reload() }
        }, "Refresh Now")
    ]);

    rootApp.$bvToast.toast(body, {
        title: "Update Available",
        solid: true,
        noAutoHide: true,
        toaster: "b-toaster-bottom-right"
    });
}

/**
 * Fetches the deployed version.json and latches updateAvailable when it differs from the
 * version compiled into this bundle. Every failure path is a silent no-op: an update check
 * must never surface errors to the user or interrupt their work.
 */
export async function checkForUpdate(): Promise<void> {
    if (!_runtime.isEnabled() || updateState.updateAvailable) return;
    lastCheckAt = Date.now();

    let info: VersionInfo;
    try {
        // no-cache (not no-store) so the browser revalidates but still gets cheap 304s via ETag.
        const response = await fetch("/version.json", { cache: "no-cache" });
        if (!response.ok) return;
        info = await response.json();
    } catch {
        return;
    }

    if (info?.version && info.version !== _runtime.currentVersion()) {
        updateState.updateAvailable = true;
        showUpdateToast();
    }
}

/**
 * A route change is a safe boundary to apply a pending update: no in-progress form state
 * exists on the destination page yet, and the user keeps their intended destination. Swap
 * the SPA navigation for a full page load so the new bundle boots at the target route.
 */
export function installUpdateNavigationGuard(router: VueRouter): void {
    if (!_runtime.isEnabled()) return;

    router.beforeEach((to, from, next) => {
        // Page chunks load after the global guards run, so this is set before any chunk can fail.
        pendingNavigationHref = router.resolve(to.fullPath).href;
        if (!updateState.updateAvailable) return next();
        // A chained guided tour navigates between pages itself. Reloading here would cut the run
        // short halfway through, so the update waits for the next ordinary navigation instead.
        // The Refresh Now toast is already on screen either way.
        if (_runtime.isTourActive()) return next();
        // An unmatched `from` includes the router's START_LOCATION, i.e. the initial navigation.
        if (!from.matched.length) return next();
        if (to.fullPath === from.fullPath) return next();
        if (recentlyForcedReload()) return next();

        markForcedReload();
        _runtime.navigate(router.resolve(to.fullPath).href);
        // No next(): the page is unloading into a full navigation.
    });

    router.afterEach(() => {
        pendingNavigationHref = null;
    });

    // afterEach skips failed navigations, so clear the destination here too, after the handler
    // has read it.
    router.onError((error) => {
        if (isChunkLoadError(error)) handleChunkLoadFailure();
        pendingNavigationHref = null;
    });
}

/** Route-level dynamic imports fail with these messages when a tab predates the current deploy. */
export function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? "");
    return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i
        .test(message);
}

/**
 * A missing chunk means this tab's bundle predates the current deploy (the CDN's SPA fallback
 * answers deleted chunk URLs with index.html, which fails as a module). One full page load picks
 * up the new bundle: at the navigation's destination when one is in flight, otherwise a reload
 * of the current page. If we already did this recently, surface the toast instead of looping.
 * Returns true when it started a full page load, false when it only surfaced the toast.
 */
export function handleChunkLoadFailure(): boolean {
    if (recentlyForcedReload()) {
        updateState.updateAvailable = true;
        showUpdateToast();
        return false;
    }
    markForcedReload();
    if (pendingNavigationHref) _runtime.navigate(pendingNavigationHref);
    else _runtime.reload();
    return true;
}

/**
 * Vite dispatches vite:preloadError when a built dynamic import fails (e.g. a chunk removed by a
 * newer deploy). preventDefault makes the import resolve to undefined, which Vue Router 3 cannot
 * handle, so suppress the error only while a full page load is replacing this page. Otherwise
 * the import rejects, Vue Router aborts the navigation, and the user stays put with the toast.
 */
export function onPreloadError(event: Event): void {
    if (handleChunkLoadFailure()) event.preventDefault();
}

/**
 * Starts the periodic/version-event checks. Called once from main.ts after the root Vue
 * instance exists (the toast and the SignalR state watch both need it).
 */
export function initUpdateCheck(app: Vue): void {
    if (!_runtime.isEnabled()) return;
    rootApp = app;

    setInterval(() => checkForUpdate(), CHECK_INTERVAL_MS);

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && Date.now() - lastCheckAt >= FOCUS_CHECK_MIN_GAP_MS) {
            checkForUpdate();
        }
    });

    // The data hub reconnecting usually means the API just restarted for a deploy, and frontend
    // deploys typically ship alongside API deploys — check promptly instead of waiting out
    // the interval.
    app.$watch(
        () => app.$data.dataHubState,
        (state: string, previous: string) => {
            if (state === "Connected" && previous && previous !== "Connected"
                && Date.now() - lastCheckAt >= RECONNECT_CHECK_MIN_GAP_MS) {
                checkForUpdate();
            }
        }
    );

    window.addEventListener("vite:preloadError", onPreloadError);

    checkForUpdate();
}

/** Test-only: reset module state between specs. */
export function _resetForTests(): void {
    rootApp = null;
    lastCheckAt = 0;
    toastShown = false;
    pendingNavigationHref = null;
    updateState.updateAvailable = false;
    try {
        sessionStorage.removeItem(FORCED_RELOAD_GUARD_KEY);
    } catch {
        // Ignore.
    }
}
